<script>
  import { createEventDispatcher } from 'svelte'
  import { importHevyCSV } from '../services/import.js'
  import { getExerciseDisplayName } from '../data/equipment-types.js'

  const dispatch = createEventDispatcher()

  let file = null
  let fileInput
  let importing = false
  let importProgress = { stage: '', progress: 0, imported: 0, total: 0 }
  let importResult = null
  let previewWorkouts = []
  let showPreview = false

  const stageLabels = {
    parsing: '解析 CSV 檔案',
    validating: '驗證資料格式',
    grouping: '整理訓練資料',
    transforming: '轉換資料格式',
    checking_duplicates: '檢查重複資料',
    importing: '匯入資料庫',
    complete: '匯入完成'
  }

  function handleFileSelect(event) {
    const selectedFile = event.target.files[0]
    if (selectedFile && selectedFile.type === 'text/csv') {
      file = selectedFile
      importResult = null
      showPreview = false
      previewWorkouts = []
    } else {
      alert('請選擇 CSV 檔案')
    }
  }

  async function handleImport() {
    if (!file) {
      alert('請先選擇檔案')
      return
    }

    importing = true
    importResult = null

    try {
      const result = await importHevyCSV(file, {
        onProgress: (progress) => {
          importProgress = progress
        },
        onValidate: async (workouts) => {
          // Show preview of first 10 workouts
          previewWorkouts = workouts.slice(0, 10)
          showPreview = true

          // Wait for user confirmation
          return new Promise((resolve) => {
            window.confirmImport = resolve
          })
        },
        skipDuplicates: true
      })

      importResult = result

      if (result.success) {
        // Dispatch success event to trigger sync
        dispatch('importSuccess', { count: result.imported })
      }
    } catch (error) {
      console.error('Import error:', error)
      importResult = {
        success: false,
        error: error.message
      }
    } finally {
      importing = false
      file = null
      if (fileInput) fileInput.value = ''
    }
  }

  function confirmImport() {
    showPreview = false
    if (window.confirmImport) {
      window.confirmImport(true)
      delete window.confirmImport
    }
  }

  function cancelImport() {
    showPreview = false
    importing = false
    if (window.confirmImport) {
      window.confirmImport(false)
      delete window.confirmImport
    }
  }

  function handleClose() {
    dispatch('close')
  }
</script>

<div class="container mx-auto px-4 py-6 max-w-4xl">
  <!-- Header -->
  <div class="flex items-center justify-between mb-6">
    <h1 class="text-2xl font-bold">匯入 Hevy 資料</h1>
    <button on:click={handleClose} class="btn btn-ghost">
      ✕
    </button>
  </div>

  <!-- Instructions -->
  <div class="card bg-blue-50 border border-blue-200 mb-6">
    <h2 class="text-lg font-semibold text-blue-900 mb-2">📝 使用說明</h2>
    <ol class="list-decimal list-inside space-y-1 text-blue-800 text-sm">
      <li>開啟 Hevy app,前往 Settings → Export Data</li>
      <li>選擇 CSV 格式並下載檔案</li>
      <li>在此頁面選擇下載的 CSV 檔案</li>
      <li>點擊「開始匯入」並確認預覽</li>
      <li>等待匯入完成,系統會自動同步至雲端</li>
    </ol>
  </div>

  <!-- File Selection -->
  <div class="card mb-6">
    <h2 class="text-lg font-semibold mb-4">選擇 CSV 檔案</h2>

    <div class="mb-4">
      <input
        bind:this={fileInput}
        type="file"
        accept=".csv"
        on:change={handleFileSelect}
        disabled={importing}
        class="w-full"
      />
    </div>

    {#if file}
      <div class="alert alert-info">
        <span>✓ 已選擇: {file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
      </div>
    {/if}

    <button
      on:click={handleImport}
      disabled={!file || importing}
      class="btn btn-primary w-full mt-4"
    >
      {#if importing}
        <span class="loading loading-spinner"></span>
        匯入中...
      {:else}
        開始匯入
      {/if}
    </button>
  </div>

  <!-- Progress -->
  {#if importing && !showPreview}
    <div class="card mb-6">
      <h2 class="text-lg font-semibold mb-4">匯入進度</h2>

      <div class="mb-2">
        <div class="flex justify-between text-sm mb-1">
          <span>{stageLabels[importProgress.stage] || importProgress.stage}</span>
          <span>{Math.round(importProgress.progress)}%</span>
        </div>
        <progress
          class="progress progress-primary w-full"
          value={importProgress.progress}
          max="100"
        ></progress>
      </div>

      {#if importProgress.stage === 'importing' && importProgress.total > 0}
        <p class="text-sm text-gray-600 mt-2">
          已匯入 {importProgress.imported} / {importProgress.total} 筆訓練
        </p>
      {/if}
    </div>
  {/if}

  <!-- Preview -->
  {#if showPreview && previewWorkouts.length > 0}
    <div class="card mb-6">
      <h2 class="text-lg font-semibold mb-4">預覽 (前 10 筆訓練)</h2>

      <div class="overflow-x-auto max-h-96 mb-4">
        <table class="table table-sm">
          <thead>
            <tr>
              <th>日期</th>
              <th>標題</th>
              <th>時長</th>
              <th>運動項目</th>
            </tr>
          </thead>
          <tbody>
            {#each previewWorkouts as workout}
              <tr>
                <td>{workout.date}</td>
                <td>{workout.title}</td>
                <td>{workout.duration ? Math.floor(workout.duration / 60) : 0} 分鐘</td>
                <td>
                  <div class="text-xs space-y-1">
                    {#each workout.exercises as exercise}
                      <div>
                        {getExerciseDisplayName(exercise.name, exercise.equipment_type)}
                        ({exercise.sets.length} 組)
                      </div>
                    {/each}
                  </div>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>

      <div class="alert alert-warning mb-4">
        <span>⚠️ 確認資料正確後,點擊「確認匯入」繼續。此操作無法復原。</span>
      </div>

      <div class="flex gap-2">
        <button on:click={confirmImport} class="btn btn-primary flex-1">
          確認匯入
        </button>
        <button on:click={cancelImport} class="btn btn-ghost flex-1">
          取消
        </button>
      </div>
    </div>
  {/if}

  <!-- Result -->
  {#if importResult}
    <div class="card">
      <h2 class="text-lg font-semibold mb-4">匯入結果</h2>

      {#if importResult.success}
        <div class="alert alert-success">
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 class="font-bold">匯入成功!</h3>
            <div class="text-sm">
              <p>已匯入 {importResult.imported} 筆訓練</p>
              {#if importResult.skipped > 0}
                <p>跳過 {importResult.skipped} 筆重複資料</p>
              {/if}
              <p class="mt-2 text-gray-600">資料已儲存至本地,請點擊右上角同步按鈕上傳至雲端。</p>
            </div>
          </div>
        </div>

        <button on:click={handleClose} class="btn btn-primary w-full mt-4">
          完成
        </button>
      {:else if importResult.cancelled}
        <div class="alert alert-warning">
          <span>{importResult.message}</span>
        </div>
      {:else}
        <div class="alert alert-error">
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 class="font-bold">匯入失敗</h3>
            <div class="text-sm">{importResult.error || importResult.message}</div>
          </div>
        </div>

        <button on:click={() => importResult = null} class="btn btn-ghost w-full mt-4">
          重試
        </button>
      {/if}
    </div>
  {/if}
</div>
